import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';

import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { CacheService } from '../global/ng2-cache/services/cache.service';

import { AccountDialogComponent } from '../account-dialog/account-dialog.component';

import { AuthService } from '../services/auth.service';
import { SessionService } from '../services/session2.service';
import { ActonService } from '../services/acton.service';
import { HubspotService } from '../services/hubspot.service';
import { MarketoService } from '../services/marketo.service';
import { EloquaService } from '../services/eloqua.service';
import { PardotService } from '../services/pardot.service';
import { SourceService } from '../services/source.service';
import { ConfigService } from '../services/config.service';
import { openChat, startInteraction, shouldLaunchHelpInteraction, saveHelpInteractionStart } from './integrated-help-utils';
import { environment } from 'environments/environment';


const BEAMER_APP_ID = 'iAguvIlC19427';

const INTEGRATED_HELP_INTERACTION_ID = environment.integratedHelpInteractionId

declare global {
  interface Window {
    beamer_config: any;
    Beamer: any;
    drift: any;
  }
}

@Component({
  selector: 'drift-nav',
  templateUrl: './drift-nav.component.html',
  styleUrls: ['./drift-nav.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DriftNavComponent implements OnInit {

  navigationSubscription; //used to force the component to reload; This happens when you switch accounts and are already on this view

  isLoggedIn: boolean = false;
  session: any = {};
  accountSettings = {};
  accountDialogRef: BsModalRef;
  nameInitials: string = '';

  psa = true;

  accountChange: boolean = false;

  emailCampaignsUrl: string = '';

  constructor(private authService: AuthService,
    private sessionService: SessionService,
    private pardotService: PardotService,
    private marketoService: MarketoService,
    private eloquaService: EloquaService,
    private hubspotService: HubspotService,
    private actonService: ActonService,
    private sourceService: SourceService,
    private cacheService: CacheService,
    private router: Router,
    private modalService: BsModalService,
    private configService: ConfigService) {

    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      // If it is a NavigationEnd event re-initalise the component
      if (e instanceof NavigationEnd) {
        this.sessionRefresh();
      }
    });

    this.configService.driftAppUrl().subscribe((driftAppUrl: string) => {
      this.emailCampaignsUrl = `${driftAppUrl}/email/campaigns`;
    });
  }

  ngOnInit() {

    this.sessionRefresh();
    this.insertBeamerScript();
  }

  ngOnDestroy() {
    // avoid memory leaks here by cleaning up after ourselves. If we
    // don't then we will continue to run our initialiseInvites()
    // method on every navigationEnd event.
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  insertBeamerScript() {
    window.beamer_config = {lazy: true};

    const scriptTag = document.createElement('script');
    scriptTag.id = 'beamer-container';
    scriptTag.src = 'https://app.getbeamer.com/js/beamer-embed.js';
    scriptTag.defer = true;
    scriptTag.type = 'text/javascript';
    scriptTag.onload = () => this.initializeBeamer();
    document.body.appendChild(scriptTag);
  }

  initializeBeamer() {
    window.beamer_config = {
      selector: '.rocket-link',
      product_id: BEAMER_APP_ID,
      button_position: 'top-left',
      top: -6,
      right: -6,
      user_firstname: this.session.name.split(' ')[0],
      user_lastname: this.session.name.split(' ')[1],
      user_email: this.session.email,
      user_id: this.session.account_id,
      filter_by_url: true,
    };

    // dynamically build available feature flags into the beamer config
    Object.keys(this.accountSettings).forEach((feature) => {
      window.beamer_config[`account_feature_${feature}`] = this.accountSettings[feature]
    })

    try {
      if (window.Beamer) {
        window.Beamer.init();
      }
    } catch (e) {
      console.warn(e);
    }
  }

  changeAccount() {
    let me = this;
    this.accountDialogRef = this.modalService.show(AccountDialogComponent, Object.assign({}, {
      initialState: {
        change: (selectedAccount) => {
          me.accountDialogRef.hide();
          if (me.cacheService.get('session').account_id !== selectedAccount.value) {
            // switching account context
            me.sourceService.clearCache(); // clear the source cache
            // clear all cached MAP stuff
            me.actonService.clearAll();
            me.eloquaService.clearAll();
            me.hubspotService.clearAll();
            me.marketoService.clearAll();
            me.pardotService.clearAll();
            me.sessionService.changeAccount(selectedAccount.value, selectedAccount.label).subscribe(
              () => {
                me.sessionService.getSession(true).subscribe((result: any) => {
                  me.session = result;
                  me.resetHomeFilters(me);
                  me.resetInboxFilters(me);
                  me.resetContactFilters(me);
                  me.resetWorkflowFilters(me);
                  me.cacheService.set('session', me.session);
                  me.router.navigateByUrl('');
                });
              },
              (err) => {
                console.error(err);
              }
            );
          }
        }
      }
    }));
  }

  triggerIntegratedHelp() {
    if (shouldLaunchHelpInteraction()) {
      startInteraction(INTEGRATED_HELP_INTERACTION_ID)
      saveHelpInteractionStart()
    } else {
      openChat()
    }
  }

  getRole(role: number) {
    if (this.authService.isLoggedIn) {
      this.session = this.cacheService.get('session');
      return this.authService.currentRole.mask >= role;
    }
    return false;
  }

  private resetInboxFilters(me: DriftNavComponent) {
    me.session.inbox_filters = {
      range: null,
      days: 30,
      types: [],
      sources: [],
      recipient: '',
      approve_status: [],
      bot_classification: [],
      current_page: 0,
      new_page: 1,
      firstId: null,
      lastId: null
    };
  }

  private resetHomeFilters(me: DriftNavComponent) {
    me.session.dashboard_filters = {
      days: 30,
      range: null
    };
  }

  private resetWorkflowFilters(me: DriftNavComponent) {
    this.session.workflow_filters = {
      type: 'all',
      wf_type: 'contacts',
      sort: {
        field: 'ls_date',
        direction: -1
      }
    };
  }

  private resetContactFilters(me: DriftNavComponent) {
    me.session.contact_filters = {
      sender: false,
      type: 'all',
      sources: [],
      quality: 0,
      days: 30,
      range: null,
      search: '',
      page: 1,
      sort: {
        field: 'ls_date',
        direction: -1
      }
    };
  }

  private async sessionRefresh() {
    if (this.authService.isLoggedIn) {
      this.isLoggedIn = true;
      this.sessionService.getSession().subscribe((data: any) => {
        this.session = data;
        this.accountSettings = data.f;
        this.nameInitials = data.name.split(' ').map((name: string) => name[0]).join('');
      });
      const accountCount = await this.authService.getAccountCount()
      if (accountCount > 1 || this.authService.currentRole.mask > 16) {
        this.accountChange = true;
      }

      if (this.authService.currentRole.title == 'uadmin') {
        //this.psa = false;
      }
    }
    else {
      this.isLoggedIn = false;
    }
  }
}



// WEBPACK FOOTER //
// ./client/app/drift-nav/drift-nav.component.ts